import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { meService, type MeWorkspacesResponse } from '@/api/services/me';
import { useMe } from '@/context/MeContext';

type WorkspaceContextValue = {
  workspaceVersion: number;
  workspaceScopeKey: string;
  workspaces: MeWorkspacesResponse | null;
  workspacesLoading: boolean;
  isPersonalMode: boolean;
  activeOrganizationId: string | null;
  activeWorkspaceName: string | null;
  canSwitchWorkspace: boolean;
  canManageOrganizations: boolean;
  selectWorkspace: (organizationId: string | null) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
  removeWorkspaceFromList: (organizationId: string) => void;
  invalidateWorkspaces: () => Promise<void>;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { me, refresh: refreshMe } = useMe();
  const [workspaceVersion, setWorkspaceVersion] = useState(0);
  const [workspaces, setWorkspaces] = useState<MeWorkspacesResponse | null>(null);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  const canSwitchWorkspace = Boolean(me?.permissions.canSwitchWorkspace);

  const isPersonalMode =
    canSwitchWorkspace && (me?.activeWorkspaceMode === 'personal' || !me?.organizationId);

  const refreshWorkspaces = useCallback(async () => {
    if (!canSwitchWorkspace) {
      setWorkspaces(null);
      return;
    }
    setWorkspacesLoading(true);
    try {
      const data = await meService.listWorkspaces();
      setWorkspaces(data);
    } finally {
      setWorkspacesLoading(false);
    }
  }, [canSwitchWorkspace]);

  useEffect(() => {
    void refreshWorkspaces();
  }, [refreshWorkspaces, workspaceVersion, me?.organizationId, me?.activeWorkspaceMode]);

  const removeWorkspaceFromList = useCallback((organizationId: string) => {
    setWorkspaces((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.filter((item) => item.id !== organizationId),
        activeOrganizationId:
          prev.activeOrganizationId === organizationId ? null : prev.activeOrganizationId,
      };
    });
  }, []);

  const bumpWorkspaceVersion = useCallback(() => {
    setWorkspaceVersion((value) => value + 1);
  }, []);

  const selectWorkspace = useCallback(
    async (organizationId: string | null) => {
      await meService.selectWorkspace(organizationId);
      await refreshMe();
      await refreshWorkspaces();
      bumpWorkspaceVersion();
    },
    [refreshMe, refreshWorkspaces, bumpWorkspaceVersion],
  );

  const invalidateWorkspaces = useCallback(async () => {
    await Promise.all([refreshWorkspaces(), refreshMe()]);
    bumpWorkspaceVersion();
  }, [refreshMe, refreshWorkspaces, bumpWorkspaceVersion]);

  const workspaceScopeKey = useMemo(() => {
    const scope = isPersonalMode ? 'personal' : me?.organizationId ?? 'workspace';
    return `${scope}:${workspaceVersion}`;
  }, [isPersonalMode, me?.organizationId, workspaceVersion]);

  const value = useMemo(
    () => ({
      workspaceVersion,
      workspaceScopeKey,
      workspaces,
      workspacesLoading,
      isPersonalMode,
      activeOrganizationId: me?.organizationId ?? null,
      activeWorkspaceName: me?.activeWorkspaceName ?? null,
      canSwitchWorkspace,
      canManageOrganizations: Boolean(
        me?.permissions.canManagePlatformUsers ?? me?.permissions.canManageUsers,
      ),
      selectWorkspace,
      refreshWorkspaces,
      removeWorkspaceFromList,
      invalidateWorkspaces,
    }),
    [
      workspaceVersion,
      workspaceScopeKey,
      workspaces,
      workspacesLoading,
      isPersonalMode,
      me,
      canSwitchWorkspace,
      selectWorkspace,
      refreshWorkspaces,
      removeWorkspaceFromList,
      invalidateWorkspaces,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
